"use client";

import { useCallback, useState } from "react";

import { useLegal } from "../../../_lib/hooks/legal";
import type { LegalKind } from "../../../_lib/siteConfig";
import { useSite } from "../../../_lib/useSite";
import { Action, Problem, Waiting } from "./parts";
import { Sheet } from "./Sheet";

/*
 * 약관 시트 — 시안 10 (`docs/design/simple-skin/sample/10-terms-mo.html`).
 *
 * 🔴 **라우트가 아니라 시트입니다**(README §5). 가입 폼을 절반 채운 채 약관을 봐도 입력값이 남습니다.
 * 🔴 **본문은 서버가 정화한 HTML** 입니다(`App\Support\HtmlSanitizer`) — 그대로 그립니다.
 *    시안의 조 제목·본문 서식(14.5 굵게 · 14/1.75 · 조 사이 18)은 `../tokens.css` 의
 *    `[data-skin="simple"] .legal-body` 가 태그에 입힙니다 — 조를 상자·목차로 나누지 않습니다
 *    (결정 기록 §5-②).
 * 🔑 `_ui/LegalSheet.tsx` 와 같은 훅(`useLegal`)을 쓰고 모양만 다릅니다(손잡이 · 닫기 아이콘 ·
 *    바닥 버튼). 가입 화면에서 열면 바닥 버튼이 «동의합니다» 로 그 항목을 켭니다.
 *
 * 사용:
 *   const legal = useSimpleLegal();
 *   <button onClick={() => legal.open("terms", () => setAgreeTerms(true))}>보기</button>
 *   {legal.sheet}
 */
export function useSimpleLegal() {
  const [state, setState] = useState<{ kind: LegalKind; onAgree?: () => void } | null>(null);
  const close = useCallback(() => setState(null), []);
  const open = useCallback((kind: LegalKind, onAgree?: () => void) => setState({ kind, onAgree }), []);
  return { open, close, sheet: <SimpleLegalSheet kind={state?.kind ?? null} onAgree={state?.onAgree} onClose={close} /> };
}

function SimpleLegalSheet({ kind, onAgree, onClose }: { kind: LegalKind | null; onAgree?: () => void; onClose: () => void }) {
  const { legal } = useSite();
  const doc = useLegal(kind);
  const entry = legal.find((item) => item.kind === kind);
  /* 🔴 다른 약관의 낡은 본문을 그리지 않습니다 — `useAsync` 는 새 응답 전까지 이전 값을 듭니다. */
  const current = doc.data && (doc.data.kind === "" || doc.data.kind === kind) ? doc.data : null;
  const title = current?.title || entry?.title || "약관";
  const effectiveOn = current?.effectiveOn ?? entry?.effectiveOn ?? null;

  return (
    <Sheet
      open={kind !== null}
      onClose={onClose}
      tall
      closeButton
      title={title}
      subtitle={effectiveOn ? <span className="tabular-nums">시행일 {effectiveOn.slice(0, 10)}</span> : undefined}
      footer={
        onAgree ? (
          <Action
            size="lg"
            tooltip="이 약관에 동의하고 가입 화면으로 돌아갑니다."
            onClick={() => {
              onAgree();
              onClose();
            }}
          >
            동의합니다
          </Action>
        ) : (
          <Action size="lg" variant="secondary" tooltip="약관 창을 닫고 하던 화면으로 돌아갑니다." onClick={onClose}>
            닫기
          </Action>
        )
      }
    >
      {(doc.loading || (!current && !doc.error)) && <Waiting />}
      {doc.error && <Problem message={doc.error} />}
      {current && !doc.loading && <div className="legal-body pb-4" dangerouslySetInnerHTML={{ __html: current.bodyHtml }} />}
    </Sheet>
  );
}
